import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReCAPTCHA from 'react-google-recaptcha';
import { accountService } from '../../Services';
import { Toast } from '../../Components';

const initForm = {
  email: "",
};

export default function ForgotPass({ setModal, setRoleId }) {
  const [form, setForm] = useState(initForm);
  const [error, setError] = useState(false);
  const [toastList, setToastList] = useState([]);
  const [formSubmit, setFormSubmit] = useState(false);
  const [searchParams] = useSearchParams();
  const [showlinkExpiredMessage] = useState(searchParams.get('expired'));
  const [captcha, setCaptcha] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
    link.crossOrigin = "anonymous";
    link.referrerPolicy = "no-referrer";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const updateForm = (data) => {
    setForm({ ...form, ...data });
  };
  const setInput = (key) => (event) =>
    updateForm({ [key]: event.target.value });

  const addToast = data => {
    const newToastList = [...toastList];
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  useEffect(() => {
    if (showlinkExpiredMessage === '1') {
      addToast({
        error: true,
        msg: "The link is no longer valid. Please try to reset your password.",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitEmail = async (payload) => {
    setLoading(true);
    try {
      await accountService
        .forgotPassworkCheck(payload)
        .then(res => {
          addToast({
            error: false,
            msg: res.message,
            date: new Date()
          });
          setFormSubmit(true);
        })
        .catch(err => {
          addToast({
            error: true,
            msg: err.replaceAll(/<\/?[^>]+(>|$)/gi, ''),
            successText: '',
            date: new Date()
          });
        });
    } catch (err) {
      addToast({ error: true, date: new Date(), msg: err.message });
    } finally {
      setLoading(false);
    }
  };

  const createValidation = () => {
    if (form.email === '' || captcha === '') {
      setError(true);
    } else {
      const payload = {
        email: form.email,
        captcha: captcha
      };
      submitEmail(payload);
    }
  };

  const onChange = value => {
    setCaptcha(value);
  };

  useEffect(() => {
    const keyDownHandler = event => {
      if (event.key === 'Enter') {
        createValidation();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, captcha]);

  // Logo component matching Login.jsx
  const Logo = () => (
    <div className="flex items-center gap-3">
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
        style={{
          background: "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
          boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
        }}
      >
        <img
          src={process.env.PUBLIC_URL + "/favicon/favicon.ico"}
          className="h-7 w-7 drop-shadow-md"
          alt="Logo"
        />
      </div>
      <span
        className="font-bold text-2xl tracking-wide"
        style={{
          background: "linear-gradient(90deg, #059669, #10b981, #34d399)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        TAMA
      </span>
    </div>
  );

  // Mockup data matching Login.jsx
  const mockupUsers = [
    { name: "Maya Philips", followers: "4.5M users", color: "bg-pink-500", initial: "M" },
    { name: "Claire Writers", followers: "4M users", color: "bg-purple-500", initial: "C" },
    { name: "Austin Karter", followers: "3M users", color: "bg-blue-500", initial: "A" },
    { name: "Abram Daloda", followers: "2.5M users", color: "bg-emerald-500", initial: "A" },
  ];

  return (
    <div className="flex min-h-screen font-sans">
      {/* Left Panel - Promotional Content - Desktop only */}
      <div className="hidden lg:flex flex-1 flex-col justify-center items-center p-8 bg-gradient-to-br from-blue-50 via-green-50 to-pink-50 lg:min-w-[50%] relative overflow-hidden">
        <div className="text-center max-w-[500px] z-10">
          {/* Heading */}
          <div className="flex items-start justify-center gap-3 mb-8">
            <span 
              className="text-4xl font-extrabold"
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              #1
            </span>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight text-left">
              End-to-End Influencer<br />Marketing Platform
            </h2>
          </div>

          {/* Dashboard Mockup */}
          <div className="bg-white rounded-2xl shadow-2xl p-6 mb-8 w-full max-w-[450px]">
            <div className="flex items-center justify-between mb-4 border-b border-gray-100 pb-4">
              <span className="text-sm font-semibold text-gray-700">Influencer Revenue Performance</span>
              <div className="flex gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Reach</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-700">Engagement</span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {mockupUsers.map((user, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-gray-400 mr-2">{index + 1}</span>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm ${user.color}`}>
                      {user.initial}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{user.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500">{user.followers}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Award-Winning Technology Section */}
          <div className="mt-10 w-full max-w-[650px]">
            <h3 className="text-lg font-semibold text-gray-800 mb-6 text-center">Award-Winning Technology</h3>
            <div className="flex items-center justify-between w-full">
              <img 
                src={process.env.PUBLIC_URL + "/images/trusted.png"} 
                alt="Trusted" 
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img 
                src={process.env.PUBLIC_URL + "/images/high_performance.png"} 
                alt="High Performance" 
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img 
                src={process.env.PUBLIC_URL + "/images/powerd_by_data.png"} 
                alt="Powered by Data" 
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img 
                src={process.env.PUBLIC_URL + "/images/recommended.png"} 
                alt="Recommended" 
                className="h-24 object-contain hover:scale-105 transition-transform duration-300"
              />
              <img 
                src={process.env.PUBLIC_URL + "/images/responsible_ai.png"} 
                alt="Responsible AI" 
                className="h-20 object-contain hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Forgot Password Form */}
      <div className="w-full lg:flex-1 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-8 bg-white lg:min-w-[50%] min-h-screen lg:min-h-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-[380px] px-2 sm:px-0"
        >
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <Logo />
          </div>

          {/* Heading */}
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2 text-center">
            Forgot Password
          </h1>
          <p className="text-gray-500 mb-6 sm:mb-8 text-sm text-center">
            Enter your email to receive a password reset link
          </p>

          {formSubmit ? (
            /* Success Message */
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6 text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-check text-green-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">Email Sent!</h3>
              <p className="text-sm text-green-700">
                If <strong>{form.email}</strong> exists, we have sent you a password reset email.
              </p>
            </div>
          ) : (
            <>
              {/* Email Input */}
              <div className="mb-4 sm:mb-5">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Email</label>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={form.email}
                  onChange={setInput("email")}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 text-sm sm:text-base border rounded-lg outline-none transition-all duration-200 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error && !form.email ? "border-red-500" : "border-gray-200"
                  }`}
                  autoFocus
                />
                {error && !form.email && (
                  <span className="text-red-500 text-xs mt-1 block">Please enter Email</span>
                )}
              </div>

              {/* reCAPTCHA */}
              <div className="mb-4 sm:mb-5">
                <div className="flex justify-center transform scale-90 sm:scale-100 origin-center">
                  <ReCAPTCHA
                    sitekey='6Ledv1skAAAAAF3Zb07lBukAj9N-sfq5wA_C7872'
                    onChange={onChange}
                  />
                </div>
                {error && captcha === "" && (
                  <span className="text-red-500 text-xs mt-2 block text-center">Please complete the captcha</span>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={createValidation}
                disabled={loading}
                className={`w-full py-2.5 sm:py-3.5 text-sm sm:text-base font-semibold text-white rounded-full transition-all duration-200 mb-6 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gray-900 hover:bg-gray-800 active:scale-[0.98]"
                }`}
              >
                {loading ? "SENDING..." : "SEND RESET LINK"}
              </button>
            </>
          )}

          {/* Back to Login Link */}
          <p className="text-center text-xs sm:text-sm text-gray-500 mt-6">
            Remember your password?{" "}
            <Link to="/account/login" className="text-indigo-500 font-medium hover:text-indigo-600">
              Sign In
            </Link>
          </p>

          {/* Footer */}
          <div className="border-t border-gray-100 mt-6 sm:mt-8 pt-4 sm:pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <span>© 2026</span>
                <a href="https://tamayou.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">Privacy Policy</a>
                <a href="https://tamayou.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">Terms of Service</a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Toast Messages */}
      {toastList.length > 0 && (
        <Toast list={toastList} setList={setToastList} />
      )}
    </div>
  );
}